// src/pages/finance-portal/reports/CashFlowStatement.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Stack, Chip,
} from '@mui/material';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency,
    firstOfYearIso, todayIso, downloadCsv, printReport,
} from './reportShared';

export default function CashFlowStatement() {
    const [start, setStart] = useState(firstOfYearIso());
    const [end, setEnd] = useState(todayIso());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/reports/cash-flow', {
                params: { start_date: start, end_date: end },
            });
            if (res.data.success) setData(res.data);
            else setError(res.data.message || 'Failed to load');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load cash flow');
        } finally { setLoading(false); }
    }, [start, end]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        if (!data) return;
        const rows = [
            { section: 'Operating Activities' },
            { section: '', line: 'Net income', amount: data.operating.net_income },
            { section: '', line: 'AR change', amount: data.operating.accounts_receivable_change },
            { section: '', line: 'Inventory change', amount: data.operating.inventory_change },
            { section: '', line: 'AP change', amount: data.operating.accounts_payable_change },
            { section: 'Total Operating', amount: data.operating.total_operating },
            { section: 'Investing Activities' },
            ...(data.investing.detail || []).map((d) => ({
                section: '', line: d.account_name, amount: d.change,
            })),
            { section: 'Total Investing', amount: data.investing.total_investing },
            { section: 'Financing Activities' },
            ...(data.financing.detail || []).map((d) => ({
                section: '', line: d.account_name, amount: d.change,
            })),
            { section: 'Total Financing', amount: data.financing.total_financing },
            { section: 'Net Change in Cash', amount: data.net_change_in_cash },
            { section: 'Free Cash Flow', amount: data.free_cash_flow },
        ];
        downloadCsv(`cash_flow_${start}_${end}.csv`, rows, ['section', 'line', 'amount']);
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <CurrencyExchangeIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Cash Flow Statement</Typography>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                        <TextField label="Start Date" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={start} onChange={(e) => setStart(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField label="End Date" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={end} onChange={(e) => setEnd(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                        <Button onClick={load} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Run</Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {data && !loading && (
                <Paper sx={{ p: 3 }} id="cf-report">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Box>
                            <Typography variant="h6">Statement of Cash Flows</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {data.period?.start_date} – {data.period?.end_date} · {data.method}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" startIcon={<PrintIcon />} onClick={() => printReport('cf-report')}>Print</Button>
                            <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>CSV</Button>
                        </Stack>
                    </Stack>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Line</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow><TableCell colSpan={2} sx={{ bgcolor: '#f3f4f6', fontWeight: 600 }}>OPERATING</TableCell></TableRow>
                                <TableRow><TableCell>Net income</TableCell><TableCell align="right">{formatCurrency(data.operating.net_income)}</TableCell></TableRow>
                                <TableRow><TableCell>Accounts receivable change</TableCell><TableCell align="right">{formatCurrency(data.operating.accounts_receivable_change)}</TableCell></TableRow>
                                <TableRow><TableCell>Inventory change</TableCell><TableCell align="right">{formatCurrency(data.operating.inventory_change)}</TableCell></TableRow>
                                <TableRow><TableCell>Accounts payable change</TableCell><TableCell align="right">{formatCurrency(data.operating.accounts_payable_change)}</TableCell></TableRow>
                                <TableRow sx={{ '& td': { borderTop: '2px solid #d1d5db', fontWeight: 600 } }}>
                                    <TableCell>Cash from Operating</TableCell>
                                    <TableCell align="right">{formatCurrency(data.operating.total_operating)}</TableCell>
                                </TableRow>

                                <TableRow><TableCell colSpan={2} sx={{ bgcolor: '#f3f4f6', fontWeight: 600 }}>INVESTING</TableCell></TableRow>
                                {(data.investing.detail || []).map((d) => (
                                    <TableRow key={`inv-${d.account_id}`}>
                                        <TableCell>{d.account_number} — {d.account_name}</TableCell>
                                        <TableCell align="right">{formatCurrency(d.change)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ '& td': { borderTop: '2px solid #d1d5db', fontWeight: 600 } }}>
                                    <TableCell>Cash from Investing</TableCell>
                                    <TableCell align="right">{formatCurrency(data.investing.total_investing)}</TableCell>
                                </TableRow>

                                <TableRow><TableCell colSpan={2} sx={{ bgcolor: '#f3f4f6', fontWeight: 600 }}>FINANCING</TableCell></TableRow>
                                {(data.financing.detail || []).map((d) => (
                                    <TableRow key={`fin-${d.account_id}`}>
                                        <TableCell>{d.account_number} — {d.account_name}</TableCell>
                                        <TableCell align="right">{formatCurrency(d.change)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ '& td': { borderTop: '2px solid #d1d5db', fontWeight: 600 } }}>
                                    <TableCell>Cash from Financing</TableCell>
                                    <TableCell align="right">{formatCurrency(data.financing.total_financing)}</TableCell>
                                </TableRow>

                                <TableRow sx={{ '& td': { borderTop: '3px double #111827', fontWeight: 700 } }}>
                                    <TableCell>Net Change in Cash</TableCell>
                                    <TableCell align="right">{formatCurrency(data.net_change_in_cash)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Chip label={`Free Cash Flow: ${formatCurrency(data.free_cash_flow)}`} />
                        <Chip label={`Net change: ${formatCurrency(data.net_change_in_cash)}`} variant="outlined" />
                    </Stack>
                </Paper>
            )}
        </Box>
    );
}
